import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './lib/AdminAuthContext';
import { Suspense, lazy, useEffect } from 'react';

import Layout from './Layout';
import Home from './pages/Home';
import BuscarCargas from './pages/BuscarCargas';
import Login from './pages/Login';
import RedefinirSenha from './pages/RedefinirSenha';
import Perfil from './pages/Perfil';
import NovaCarga from './pages/NovaCarga';
import MinhasCargas from './pages/MinhasCargas';
import CargaDetalhes from './pages/CargaDetalhes';
import QuemSomos from './pages/QuemSomos';
import CadastroEmpresa from './pages/CadastroEmpresa';
import CadastroMotorista from './pages/CadastroMotorista';
import PainelMotorista from './pages/PainelMotorista';
import ContratoCarga from './pages/ContratoCarga';
import ProtecaoCadastro from './components/ProtecaoCadastro';
import PagamentoMercadoPago from './pages/PagamentoMercadoPago';
import PainelEspera from './pages/PainelEspera';
import BorinhoChat from './components/BorinhoChat';

// ✅ NOVO: Página de Agradecimento após contrato assinado
import Agradecimento from './pages/Agradecimento';

// ✅ NOVO: Meus Veículos
import MeusVeiculos from './pages/MeusVeiculos';

// ✅ NOVO: Gerenciadora de Risco
import GerenciadoraRisco from './pages/GerenciadoraRisco';

// ✅ NOVO: Página de frete por estado (SEO)
import FreteEstado from './pages/FreteEstado';

// ✅ NOVO: Página pública de frete com slug SEO-friendly
import FretePublico from './pages/FretePublico';

// ✅ NOVO: Editar Carga
import EditarCarga from './pages/EditarCarga';

// ✅ NOVO: Páginas de SEO para cidades e tipos de carga
import CidadeSEO from './pages/CidadeSEO';
import TipoCargaSEO from './pages/TipoCargaSEO';

// ✅ Fluxo de login Google — AuthCallback tem SelecionarTipo INLINE
import AuthCallback from './pages/AuthCallback';
import SelecionarTipo from './pages/SelecionarTipo';

// ✅ PÁGINAS DE CONTEÚDO (Footer links)
import PoliticaPrivacidade from './pages/PoliticaPrivacidade';
import TermosDeUso from './pages/TermosDeUso';
import TermoResponsabilidade from './pages/TermoResponsabilidade';
import TermosSegurancaCarga from './pages/TermosSegurancaCarga';

// ==========================================
// ADMIN — Lazy imports com CAMINHO CORRETO
// ==========================================
const AdminLogin = lazy(() => import('./admin/pages/AdminLogin'));
const AdminLayout = lazy(() => import('./admin/components/layout/AdminLayout'));
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard'));
const AdminCadastroEmpresa = lazy(() => import('./admin/pages/AdminCadastroEmpresa'));
const AdminCadastroMotorista = lazy(() => import('./admin/pages/AdminCadastroMotorista'));
const AdminDocumentos = lazy(() => import('./admin/pages/AdminDocumentos'));
const AdminCargasPendentes = lazy(() => import('./admin/pages/AdminCargasPendentes'));
const AdminPagamentos = lazy(() => import('./admin/pages/AdminPagamentos'));
const AdminComunicados = lazy(() => import('./admin/Comunicados/AdminComunicados'));

// ✅ NOVO: Admin Gerenciadoras de Risco
const AdminGerenciadorasRisco = lazy(() => import('./admin/pages/AdminGerenciadorasRisco'));

// ✅ NOVO: Admin Consultas de Cargas
const AdminConsultasCargas = lazy(() => import('./admin/pages/AdminConsultasCargas'));

// ✅ NOVO: Admin Clientes
const AdminClientes = lazy(() => import('./admin/pages/AdminClientes'));

const PageLoader = () => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

const ProtectedAdminRoute = () => {
  const { isAuthenticated, isLoading } = useAdminAuth();
  const location = useLocation();
  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/admin/login" state={{ from: location }} replace />;
  return <Outlet />;
};

// ✅ CORRIGIDO: ProtectedRoute SEM redirecionamento automático para /selecionar-tipo
// O AuthCallback já controla o fluxo completo do Google OAuth
const ProtectedRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  
  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  
  // ❌ REMOVIDO: Não redireciona mais para /selecionar-tipo aqui
  // O AuthCallback já faz isso corretamente com SelecionarTipo INLINE
  
  return <Outlet />;
};

const PageWithLayout = ({ children, pageName }) => (
  <Layout currentPageName={pageName}>{children}</Layout>
);
const PageWithoutLayout = ({ children }) => <>{children}</>;

// ✅ Home simples — SEM redirecionamento automático
// O AuthCallback controla tudo após login Google
const HomeSimple = () => {
  const { isLoading } = useAuth();
  
  if (isLoading) return <PageLoader />;
  
  return <Home />;
};

// ✅ FIX: Componente que redireciona para carga-preview.html (URL bonita sem .html)
// Agora com useEffect importado corretamente
const CargaPreviewRedirect = () => {
  useEffect(() => {
    // Pega a query string atual e redireciona para o HTML estático
    const params = window.location.search;
    window.location.href = `/carga-preview.html${params}`;
  }, []);
  
  return <PageLoader />;
};

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <AdminAuthProvider>
          <div className="min-h-screen bg-gray-50">
            <Routes>

              {/* ==========================================
                  ADMIN LOGIN (público)
              ========================================== */}
              <Route path="/admin/login" element={
                <Suspense fallback={<PageLoader />}>
                  <PageWithoutLayout><AdminLogin /></PageWithoutLayout>
                </Suspense>
              } />

              {/* ==========================================
                  ADMIN PROTEGIDO — Todas as rotas aninhadas
              ========================================== */}
              <Route element={<ProtectedAdminRoute />}>
                <Route path="/admin/*" element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminLayout />
                  </Suspense>
                }>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="empresas" element={<AdminCadastroEmpresa />} />
                  <Route path="motoristas" element={<AdminCadastroMotorista />} />
                  <Route path="documentos" element={<AdminDocumentos />} />
                  <Route path="cargas-pendentes" element={<AdminCargasPendentes />} />
                  <Route path="pagamentos" element={<AdminPagamentos />} />
                  <Route path="comunicados" element={<AdminComunicados />} />
                  
                  {/* ✅ NOVO: Rota Admin Gerenciadoras de Risco */}
                  <Route path="gerenciadoras-risco" element={
                    <Suspense fallback={<PageLoader />}>
                      <AdminGerenciadorasRisco />
                    </Suspense>
                  } />
                  
                  {/* ✅ NOVO: Rota Admin Consultas de Cargas */}
                  <Route path="consultas-cargas" element={
                    <Suspense fallback={<PageLoader />}>
                      <AdminConsultasCargas />
                    </Suspense>
                  } />
                  
                  {/* ✅ NOVO: Rota Admin Clientes */}
                  <Route path="clientes" element={
                    <Suspense fallback={<PageLoader />}>
                      <AdminClientes />
                    </Suspense>
                  } />
                </Route>
              </Route>

              {/* ==========================================
                  PÚBLICO — TODAS AS ROTAS
              ========================================== */}
              {/* ✅ Home simples — SEM redirecionamento automático */}
              <Route path="/" element={<PageWithLayout pageName="Home"><HomeSimple /></PageWithLayout>} />
              <Route path="/buscar-cargas" element={<PageWithLayout pageName="BuscarCargas"><BuscarCargas /></PageWithLayout>} />
              
              {/* ✅ Página pública de frete com slug SEO-friendly (JÁ EXISTIA) */}
              <Route path="/frete/:slug" element={<PageWithLayout pageName="FretePublico"><FretePublico /></PageWithLayout>} />
              
              {/* ✅ Páginas de frete por estado (JÁ EXISTIA) */}
              <Route path="/frete-:estadoSlug" element={<PageWithLayout pageName="FreteEstado"><FreteEstado /></PageWithLayout>} />
              
              {/* ==========================================
                  ✅ NOVO: ROTAS SEO PARA APARECER NO GOOGLE
              ========================================== */}
              {/* Página de cidade — ex: /cidade/campo-verde-mt */}
              <Route path="/cidade/:cidadeSlug" element={<PageWithLayout pageName="CidadeSEO"><CidadeSEO /></PageWithLayout>} />
              
              {/* Página de tipo de carga — ex: /tipo-carga/soja */}
              <Route path="/tipo-carga/:tipoSlug" element={<PageWithLayout pageName="TipoCargaSEO"><TipoCargaSEO /></PageWithLayout>} />
              
              {/* Página de carga por tipo — ex: /carga-soja, /carga-milho */}
              <Route path="/carga-:tipoSlug" element={<PageWithLayout pageName="TipoCargaSEO"><TipoCargaSEO /></PageWithLayout>} />
              
              {/* Página de frete por estado — ex: /frete-mato-grosso, /cargas-sao-paulo */}
              <Route path="/cargas-:estadoSlug" element={<PageWithLayout pageName="FreteEstado"><FreteEstado /></PageWithLayout>} />
              <Route path="/transporte-:estadoSlug" element={<PageWithLayout pageName="FreteEstado"><FreteEstado /></PageWithLayout>} />
              
              {/* Página de veículo — ex: /veiculo-truck, /veiculo-carreta */}
              <Route path="/veiculo-:veiculoSlug" element={<PageWithLayout pageName="FreteEstado"><FreteEstado /></PageWithLayout>} />

              {/* ✅ FIX: Rota para preview de carga (URL bonita sem .html) */}
              <Route path="/carga-preview" element={<PageWithoutLayout><CargaPreviewRedirect /></PageWithoutLayout>} />

              <Route path="/quem-somos" element={<PageWithLayout pageName="QuemSomos"><QuemSomos /></PageWithLayout>} />
              <Route path="/login" element={<PageWithoutLayout><Login /></PageWithoutLayout>} />
              <Route path="/redefinir-senha" element={<PageWithoutLayout><RedefinirSenha /></PageWithoutLayout>} />
              
              {/* ✅ ROTAS DE CADASTRO — públicas */}
              <Route path="/cadastro-empresa" element={<PageWithoutLayout><CadastroEmpresa /></PageWithoutLayout>} />
              <Route path="/cadastro-motorista" element={<PageWithoutLayout><CadastroMotorista /></PageWithoutLayout>} />

              {/* ✅ FLUXO GOOGLE */}
              <Route path="/auth/callback" element={<PageWithoutLayout><AuthCallback /></PageWithoutLayout>} />
              <Route path="/selecionar-tipo" element={<PageWithoutLayout><SelecionarTipo /></PageWithoutLayout>} />

              {/* ✅ PÁGINAS DE CONTEÚDO */}
              <Route path="/politica-privacidade" element={<PageWithLayout pageName="Politica"><PoliticaPrivacidade /></PageWithLayout>} />
              <Route path="/termos-de-uso" element={<PageWithLayout pageName="Termos"><TermosDeUso /></PageWithLayout>} />
              <Route path="/termo-responsabilidade" element={<PageWithLayout pageName="Responsabilidade"><TermoResponsabilidade /></PageWithLayout>} />
              <Route path="/termo-seguranca-carga" element={<PageWithLayout pageName="Seguranca"><TermosSegurancaCarga /></PageWithLayout>} />
              <Route path="/protecao-cargas" element={<Navigate to="/termo-seguranca-carga" replace />} />

              {/* ==========================================
                  PROTEGIDO (usuários logados) — SEM MUDANÇAS
              ========================================== */}
              <Route element={<ProtectedRoute />}>
                <Route path="/perfil" element={<PageWithLayout pageName="Perfil"><Perfil /></PageWithLayout>} />
                
                <Route path="/nova-carga" element={<PageWithLayout pageName="NovaCarga"><ProtecaoCadastro tipoRequerido="empresa"><NovaCarga /></ProtecaoCadastro></PageWithLayout>} />
                <Route path="/minhas-cargas" element={<PageWithLayout pageName="MinhasCargas"><ProtecaoCadastro tipoRequerido="empresa"><MinhasCargas /></ProtecaoCadastro></PageWithLayout>} />
                
                <Route path="/editar-carga/:id" element={<PageWithLayout pageName="EditarCarga"><ProtecaoCadastro tipoRequerido="empresa"><EditarCarga /></ProtecaoCadastro></PageWithLayout>} />
                
                <Route path="/painel" element={<PageWithLayout pageName="PainelMotorista"><ProtecaoCadastro tipoRequerido="motorista"><PainelMotorista /></ProtecaoCadastro></PageWithLayout>} />
                <Route path="/carga/:id" element={<PageWithLayout pageName="CargaDetalhes"><CargaDetalhes /></PageWithLayout>} />
                <Route path="/pagamento-mercadopago" element={<PageWithoutLayout><PagamentoMercadoPago /></PageWithoutLayout>} />
                <Route path="/contrato/:id" element={<PageWithLayout pageName="Contrato"><ProtecaoCadastro tipoRequerido="motorista"><ContratoCarga /></ProtecaoCadastro></PageWithLayout>} />
                <Route path="/painel-espera" element={<PageWithLayout pageName="PainelEspera"><ProtecaoCadastro tipoRequerido="motorista"><PainelEspera /></ProtecaoCadastro></PageWithLayout>} />
                
                <Route path="/agradecimento" element={<PageWithLayout pageName="Agradecimento"><Agradecimento /></PageWithLayout>} />
                
                <Route path="/meus-veiculos" element={
                  <PageWithLayout pageName="MeusVeiculos">
                    <ProtecaoCadastro tipoRequerido="motorista">
                      <MeusVeiculos />
                    </ProtecaoCadastro>
                  </PageWithLayout>
                } />
                
                <Route path="/gerenciadora-risco" element={
                  <PageWithLayout pageName="GerenciadoraRisco">
                    <ProtecaoCadastro tipoRequerido="motorista">
                      <GerenciadoraRisco />
                    </ProtecaoCadastro>
                  </PageWithLayout>
                } />
              </Route>

              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <BorinhoChat />
          </div>
        </AdminAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;